import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Trophy, Flame, Play } from "lucide-react";
import AppLayout from "../components/AppLayout";
import CourseCard from "../components/CourseCard";
import { courses, currentUser as fallbackUser, weeklyHours } from "../data/mockData";
import { useAuth } from "../contexts/AuthContext";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function Dashboard() {
    const { user } = useAuth();
    const display = user || fallbackUser;
    const enrolled = courses.filter((c) => c.enrolled);
    const recommended = courses.filter((c) => !c.enrolled).slice(0, 3);
    const continueCourse = enrolled[0];

    const stats = [
        { label: "Hours learned", value: "78.4", icon: Clock, accent: "text-lime" },
        { label: "Courses in progress", value: enrolled.length, icon: Play, accent: "text-grape" },
        { label: "Certificates earned", value: "3", icon: Trophy, accent: "text-lime" },
        { label: "Day streak", value: "12", icon: Flame, accent: "text-grape" },
    ];

    return (
        <AppLayout searchPlaceholder="Search courses, lessons...">
            {/* Welcome */}
            <div className="mb-10 fade-up">
                <div className="text-sm text-gray-500">Good morning,</div>
                <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-1">
                    {display.name.split(" ")[0]} <span className="text-grape">.</span>
                </h1>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {stats.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <div key={i} data-testid={`stat-${i}`} className="bg-ink-700 rounded-2xl border border-white/5 p-5 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                            <Icon className={`w-5 h-5 ${s.accent} mb-3`} />
                            <div className="font-display font-black text-3xl">{s.value}</div>
                            <div className="text-xs text-gray-500 mt-1">{s.label}</div>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-10">
                {/* Continue Learning */}
                {continueCourse && (
                    <div className="xl:col-span-2 relative overflow-hidden bg-ink-700 rounded-3xl border border-white/5 p-7">
                        <div className="absolute inset-0 opacity-30">
                            <img src={continueCourse.cover} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-r from-ink-700 via-ink-700/90 to-transparent" />
                        </div>
                        <div className="relative">
                            <div className="text-xs uppercase tracking-widest text-lime font-semibold">Continue learning</div>
                            <h2 className="font-display font-black text-3xl lg:text-4xl mt-2 max-w-md leading-tight">
                                {continueCourse.title}
                            </h2>
                            <div className="mt-5 flex items-center gap-3 text-sm text-gray-400">
                                <img src={continueCourse.instructor.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
                                <span>{continueCourse.instructor.name}</span>
                                <span className="text-gray-600">•</span>
                                <span>{continueCourse.durationHours}h total</span>
                            </div>
                            <div className="mt-6 max-w-md">
                                <div className="flex items-center justify-between text-xs mb-2">
                                    <span className="text-gray-400">Lesson 4 of 8 — Embus / Debus & Vehicle Cover</span>
                                    <span className="text-lime font-bold">{continueCourse.progress}%</span>
                                </div>
                                <div className="h-2 rounded-full bg-ink-600 overflow-hidden">
                                    <div className="h-full rounded-full bg-gradient-to-r from-grape to-lime" style={{ width: `${continueCourse.progress}%` }} />
                                </div>
                            </div>
                            <Link to={`/learn/${continueCourse.id}`} data-testid="continue-learning-btn" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-lime text-ink-900 font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5">
                                Resume <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                )}

                {/* Weekly chart */}
                <div className="bg-ink-700 rounded-3xl border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-1">
                        <h3 className="font-display font-bold text-lg">Weekly hours</h3>
                        <span className="text-xs text-gray-500">Last 8 days</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">Total: <span className="text-lime font-semibold">34.8 hours</span></p>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                            <BarChart data={weeklyHours} barCategoryGap={8}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="day" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis hide />
                                <Tooltip
                                    cursor={{ fill: "rgba(255,255,255,0.04)" }}
                                    contentStyle={{ background: "#0E0F12", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", fontSize: 12 }}
                                />
                                <Bar dataKey="completed" stackId="a" fill="#7C5CFF" radius={[0, 0, 0, 0]} />
                                <Bar dataKey="inProgress" stackId="a" fill="#CFFF50" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-grape" /> Completed</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-lime" /> In progress</span>
                    </div>
                </div>
            </div>

            {/* In Progress */}
            <div className="mb-10">
                <div className="flex items-end justify-between mb-5">
                    <h2 className="font-display font-bold text-2xl">In progress</h2>
                    <Link to="/my-courses" data-testid="view-all-my-courses" className="text-sm text-lime font-semibold hover:underline">View all</Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {enrolled.map((c) => <CourseCard key={c.id} course={c} showProgress />)}
                </div>
            </div>

            {/* Recommended */}
            <div>
                <div className="flex items-end justify-between mb-5">
                    <div>
                        <h2 className="font-display font-bold text-2xl">Recommended for you</h2>
                        <p className="text-sm text-gray-500 mt-1">Based on your role in close protection and travel safety</p>
                    </div>
                    <Link to="/catalog" data-testid="explore-catalog" className="text-sm text-lime font-semibold hover:underline">Explore catalog</Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {recommended.map((c) => <CourseCard key={c.id} course={c} />)}
                </div>
            </div>
        </AppLayout>
    );
}
